"use client";

import React, { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { Search, ArrowRight, CornerDownLeft } from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import navIndex from "@/data/nav/categoryIndex.json";
import { getCategoryUrl, getSubcategoryUrl } from "@/lib/categoryUrls";
import { getSubcategoryIcon } from "@/lib/subcategoryIcons";

// Flatten hierarchy for quick search
interface SearchEntry {
  type: "category" | "subcategory";
  name: string;
  slug: string;
  url: string;
  description: string;
  icon?: string;
  parent?: string;
}

const buildSearchPool = (): SearchEntry[] => {
  const list: SearchEntry[] = [];
  for (const cat of navIndex.categories) {
    list.push({
      type: "category",
      name: cat.name,
      slug: cat.slug,
      url: getCategoryUrl(cat.slug),
      description: cat.description,
      icon: cat.icon,
    });
    for (const sub of cat.subcategories) {
      list.push({
        type: "subcategory",
        name: sub.name,
        slug: sub.slug,
        url: getSubcategoryUrl(cat.slug, sub.slug),
        description: sub.description,
        icon: getSubcategoryIcon(sub.name || sub.slug, cat.slug),
        parent: cat.name,
      });
    }
  }
  return list;
};

const searchPool = buildSearchPool();

// Command Dialog (⌘K)
export function CategorySearchDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const router = useRouter();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  const onSelect = (targetUrl: string) => {
    onOpenChange(false);
    router.push(targetUrl);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Type a category name (e.g. Elf, Dragon, Cyberpunk)..." />
      <CommandList>
        <CommandEmpty>No generators found. Try another search.</CommandEmpty>
        <CommandGroup heading="Popular Categories & Generators">
          {searchPool.map((item) => (
            <CommandItem
              key={`${item.type}-${item.slug}`}
              value={`${item.name} ${item.parent || ""} ${item.description}`}
              onSelect={() => onSelect(item.url)}
              className="flex items-center justify-between py-3 px-3 cursor-pointer rounded-xl"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{item.icon || "✨"}</span>
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <span>{item.name}</span>
                    {item.parent && (
                      <Badge variant="outline" className="text-[10px] font-normal py-0">
                        {item.parent}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                    {item.description}
                  </p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-50" />
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

// Inline Hero Search Bar
export default function CategorySearch() {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);

  const filtered = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase();
    return searchPool
      .filter(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.slug.toLowerCase().includes(q) ||
          (item.parent && item.parent.toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [query]);

  const handleNavigate = (targetUrl: string) => {
    router.push(targetUrl);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length > 0) {
        handleNavigate(filtered[0].url);
      }
    }
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto z-30">
      <div className="relative flex items-center">
        <Search className="w-5 h-5 text-primary absolute left-4 pointer-events-none" />
        <Input
          type="text"
          placeholder="Search name generators (e.g. Dragon, Tavern, Sci-Fi, Elves)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 200)}
          onKeyDown={handleKeyDown}
          className="w-full h-14 pl-12 pr-12 rounded-2xl border-2 border-border/80 bg-card/90 shadow-xl backdrop-blur-xl text-base focus-visible:ring-primary focus-visible:border-primary transition-all text-foreground"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-4 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>

      {/* Autocomplete Dropdown */}
      {isFocused && filtered.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover/95 backdrop-blur-2xl rounded-2xl border border-border/80 shadow-2xl overflow-hidden divide-y divide-border/40 z-50">
          {filtered.map((item) => (
            <button
              key={`${item.type}-${item.slug}`}
              onClick={() => handleNavigate(item.url)}
              className="w-full flex items-center justify-between p-3.5 hover:bg-primary/10 transition-colors text-left group"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{item.icon || "✨"}</span>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-foreground group-hover:text-primary transition-colors">
                      {item.name}
                    </span>
                    {item.parent && (
                      <Badge variant="outline" className="text-[10px] py-0">
                        {item.parent}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-1">
                    {item.description}
                  </p>
                </div>
              </div>
              <CornerDownLeft className="w-4 h-4 text-muted-foreground/50 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
